import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { startInterview, sendMessage, getInterview, toggleInterviewPause, endInterview } from '../services/interviewService';
import '../styles/Chatbot.css';
import '../styles/Interview.css';
import './feedback.js';


const Interview = () => {
  const { interviewId } = useParams();
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [timer, setTimer] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [hasSpokenInitial, setHasSpokenInitial] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const [speechQueue, setSpeechQueue] = useState([]); // Queue for speech synthesis
  const [speechTranscript, setSpeechTranscript] = useState(''); // Separate state for speech recognition
  const [isSpeechPaused, setIsSpeechPaused] = useState(false); // Track paused state for speech synthesis

  // Add state for session check
  const [isSessionValid, setIsSessionValid] = useState(false);

  // Add state for pause status
  const [isPaused, setIsPaused] = useState(false);

  // Add new state for speech
  const [isSpeaking, setIsSpeaking] = useState(false);

  // Add state for feedback
  const [feedback, setFeedback] = useState(null);
  const [isEnding, setIsEnding] = useState(false);

  //blur toggle
  const [isBlurred, setIsBlurred] = useState(false);
  const [showLoginDialog, setShowLoginDialog] = useState(false);

  const [speechRate, setSpeechRate] = useState(1.0);
  const [preferredVoice, setPreferredVoice] = useState(null);

  console.log('Route params:', { interviewId });
  console.log('Current location:', location);
  console.log('Interview ID from params:', interviewId);

  // Format time from seconds to MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // useEffect(() => {
  //   const loadVoices = () => {
  //     const voices = window.speechSynthesis.getVoices();
  //     const nicky = voices.find(
  //       (v) => v.name === 'Nicky' && v.lang === 'en-US'
  //     );
  //     if (nicky) {
  //       setPreferredVoice(nicky);
  //     }
  //   };
  
  //   // Some browsers (like Chrome) require this event to detect voices
  //   window.speechSynthesis.onvoiceschanged = loadVoices;
  //   loadVoices(); // Try once immediately in case voices are already available
  // }, []);

  // Modify the speakOnce function to track speaking state and manage speech queue
  const speakOnce = useCallback((text) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'en-US';
    utterance.rate = speechRate; // Slider value for speech rate
    utterance.pitch = 1.0;

    if (preferredVoice) {
      utterance.voice = preferredVoice;
    }

    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => {
      setIsSpeaking(false);
      setSpeechQueue((prevQueue) => prevQueue.slice(1));
    };

    setSpeechQueue((prevQueue) => [...prevQueue, utterance]);

    if (!isSpeaking && !isSpeechPaused) {
      window.speechSynthesis.speak(utterance);
    }
  }, [isSpeaking, isSpeechPaused, preferredVoice]);

  useEffect(() => {
    if (!isSpeaking && !isSpeechPaused && speechQueue.length > 0) {
      window.speechSynthesis.speak(speechQueue[0]);
    }
  }, [speechQueue, isSpeaking, isSpeechPaused]);

  // Timer effect
  useEffect(() => {
    let interval;
    if (isTimerRunning) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTimerRunning]);

  // Effect to start timer when interview begins
  useEffect(() => {
    if (messages.length > 0 && !isTimerRunning) {
      setIsTimerRunning(true);
    }
  }, [messages.length, isTimerRunning]);

  useEffect(() => {
    // Check session immediately when component mounts
    const storedInterviewId = sessionStorage.getItem('interviewId');
    const urlInterviewId = interviewId;
    const effectiveInterviewId = urlInterviewId || storedInterviewId;
    
    console.log('Checking session:', { storedInterviewId, urlInterviewId, effectiveInterviewId });
    
    if (!effectiveInterviewId) {
      console.error('No interview ID found');
      navigate('/', { replace: true });
      return;
    }
    
    // If we got the ID from sessionStorage but not from URL, update the URL
    if (storedInterviewId && !urlInterviewId) {
      navigate(`/interview/${storedInterviewId}`, { replace: true });
      return;
    }
    
    setIsSessionValid(true);
  }, [interviewId, navigate]);

  // Initialize interview only after session is validated
  useEffect(() => {
    if (!isSessionValid) return;

    const initializeInterview = async () => {
      setIsLoading(true);
      try {
        // First try to get existing interview data
        const existingInterview = await getInterview(interviewId);
        
        if (existingInterview && existingInterview.conversation?.length > 0) {
          console.log('Found existing interview:', existingInterview);
          setMessages(existingInterview.conversation);
          setIsInitializing(false);
          setIsLoading(false);
          setIsTimerRunning(true);
          return;
        }

        // If no existing conversation, start a new one
        console.log('Starting new interview conversation');
        const response = await sendMessage(interviewId, 'start');
        
        if (response && response.message) {
          const initialMessage = {
            role: 'assistant',
            content: response.message,
            timestamp: new Date()
          };
          setMessages([initialMessage]);
          setIsTimerRunning(true);
          
          // Start speech after a short delay
          setTimeout(() => {
            window.speechSynthesis.cancel();
            speakOnce(response.message);
          }, 100);
        } else {
          throw new Error('No response message received');
        }
      } catch (err) {
        console.error('Failed to initialize interview:', err);
        setError('Failed to load interview. Please try refreshing the page.');
      } finally {
        setIsInitializing(false);
        setIsLoading(false);
      }
    };

    initializeInterview();
  }, [interviewId, isSessionValid, speakOnce]);

  // Effect to handle speech when messages change
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (messages.length > 0 && lastMessage.role === 'assistant' && !isSpeaking) {
      // For initial message
      if (messages.length === 1 && !hasSpokenInitial) {
        window.speechSynthesis.cancel();
        speakOnce(lastMessage.content);
        setHasSpokenInitial(true);
      }
      // For subsequent messages - only speak if it's a new message
      else if (messages.length > 1) {
        // Check if this is a new message that hasn't been spoken yet
        const messageTimestamp = lastMessage.timestamp ? new Date(lastMessage.timestamp) : new Date();
        const isRecentMessage = (new Date() - messageTimestamp) < 5000; // Within last 5 seconds
        
        if (isRecentMessage) {
          window.speechSynthesis.cancel();
          speakOnce(lastMessage.content);
        }
      }
    }
  }, [messages, speakOnce, hasSpokenInitial, isSpeaking]);

  // Handle sending messages
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!currentMessage.trim() || isLoading) return;

    setIsLoading(true);
    const userMsg = { role: 'user', content: currentMessage };
    setMessages((prev) => [...prev, userMsg]);
    setCurrentMessage('');

    const response = await sendMessage(interviewId, currentMessage);
    setIsLoading(false);

    if (response?.requiresLogin) {
      setShowLoginDialog(true);
      //setTimeout(() => navigate('/login'), 3000);
      return;
    }

    if (response?.message) {
      const aiMsg = { role: 'assistant', content: response.message };
      setMessages((prev) => [...prev, aiMsg]);
    }
  };

  const handleStartRecording = () => {
    if (!('webkitSpeechRecognition' in window)) {
      alert('Speech recognition is not supported in this browser. Please use Chrome.');
      return;
    }

    setIsRecording(true);
    const recognition = new window.webkitSpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map((result) => result[0].transcript)
        .join('');
      setSpeechTranscript(transcript);
    };

    recognition.start();
    window.recognition = recognition;
  };

  const handleStopRecording = () => {
    setIsRecording(false);
    if (window.recognition) {
      window.recognition.stop();
    }
  };

  const handlePauseResume = async () => {
    try {
      const newPausedState = !isPaused;
      setIsPaused(newPausedState);
      setIsSpeechPaused(newPausedState);

      if (newPausedState) {
        setIsTimerRunning(false);
        window.speechSynthesis.pause();
      } else {
        setIsTimerRunning(true);
        window.speechSynthesis.resume();
      }

      const response = await toggleInterviewPause(interviewId);
      if (!response || !response.success) {
        throw new Error('Failed to update pause status');
      }
    } catch (error) {
      console.error('Error toggling pause:', error);
      setError('Failed to pause/resume the interview. Please try again.');
    }
  };

  // Add cleanup effect for speech synthesis
  useEffect(() => {
    return () => {
      window.speechSynthesis.cancel();
    };
  }, []);

  // Add handler for ending interview
  const handleEndInterview = async () => {
    console.log('Clicked End Interview');
    if (window.confirm('Are you sure you want to end this interview? You won\'t be able to continue it.')) {
      setIsEnding(true);
      try {
        const response = await endInterview(interviewId);
        if (response && response.feedback) {
          setFeedback(response.feedback);
          // Stop the timer
          setIsTimerRunning(false);
        } else {
          throw new Error('No feedback received');
        }
      } catch (error) {
        console.error('Error ending interview:', error);
        setError('Failed to end the interview. Please try again.');
        setIsEnding(false);
      }
    }
  };

  // Convert message roles to the format expected by the ChatBot UI
  const formatMessagesForChatbot = () => {
    return messages.map(msg => ({
      text: msg.content,
      sender: msg.role === 'assistant' ? 'bot' : 'user'
    }));
  };

  if (feedback) {
    return (
      <div className="feedback-container">
        <h2>Interview Feedback</h2>
        <div className="feedback-content">
          <p>{feedback}</p>
        </div>
        <button 
          className="button primary"
          onClick={() => navigate('/')}
        >
          Return Home
        </button>
      </div>
    );
  }

  return (
    <div className="interview-container">
      <div className="chatbot-container">
        <div className="chatbot-header">
          <h2>Zach - AI Case Interviewer</h2>
          <div className="interview-controls">
            <div className="timer">{formatTime(timer)}</div>
            

            <button 
              className="control-button"
              onClick={() => setIsBlurred(!isBlurred)}
              disabled={isEnding}
            >
              {isBlurred ? "Show Zach's Response" : "Blur Zach's Response"}
            </button>



            <button 
              className={`control-button ${isPaused ? 'resume' : 'pause'}`}
             // onClick={handlePauseResume}
              disabled={isEnding}
            >
              {isPaused ? 'Resume' : 'Pause'}
            </button>
            <button 
              className="control-button end"
              onClick={handleEndInterview}
              disabled={isEnding}
            >
              End Interview
            </button>
          </div>
        </div>

        <div className="chatbot-messages">


          {formatMessagesForChatbot().map((message, index) => (
          <div key={index} className={`message ${message.sender}`}>
          <div
            className={`message-bubble ${
            isBlurred && message.sender === 'bot' ? 'blurred' : ''
          }`}
          >
           {isBlurred && message.sender === 'bot'
          ? 'Zach’s response is hidden.'
          : message.text}
          </div>
        </div>
        ))}



          {isLoading && (
            <div className="message bot">
              <div className="message-bubble typing">
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
              </div>
            </div>
          )}
        </div>

        <form className="chatbot-input" onSubmit={handleSendMessage}>
          <input
            type="text"
            value={currentMessage}
            onChange={(e) => setCurrentMessage(e.target.value)}
            placeholder="Type your message here..."
            disabled={isLoading || isPaused || isEnding}
          />
          <div className="input-buttons">
            {!isRecording ? (
              <button 
                type="button"
                className="mic-button"
                onClick={handleStartRecording}
                disabled={isLoading || isPaused || isEnding}
              >
                <i className="fas fa-microphone"></i>
              </button>
            ) : (
              <button 
                type="button"
                className="mic-button recording"
                onClick={handleStopRecording}
              >
                <i className="fas fa-stop"></i>
              </button>
            )}
            <button 
              type="submit" 
              disabled={!currentMessage.trim() || isLoading || isPaused || isEnding}
            >
              Send
            </button>
          </div>
        </form>
        <div className="speech-rate-slider">
          <label htmlFor="speechRate">Speech Rate: {speechRate.toFixed(2)}</label>
          <input
            id="speechRate"
            type="range"
            min="0.5"
            max="2"
            step="0.01"
            value={speechRate}
            onChange={e => setSpeechRate(Number(e.target.value))}
          />
        </div>
      </div>
      {/* Login dialog */}
      {showLoginDialog && (
        <div className="login-dialog-overlay">
          <div className="login-dialog">
            <h3>Login Required</h3>
            <p>You need to log in to continue the interview.</p>
            <p>Redirecting you to the login page...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Interview;